import { useEffect, useRef, useState } from "react";
import {
  DETERRENCE_TEST_HOLD_MS,
  DETERRENCE_TEST_SENT_MESSAGE,
  emptyDeterrenceTestState,
  finishDeterrenceTest,
  isDeterrenceTestBusy,
  releaseLapsedDeterrenceTests,
  startDeterrenceTest,
  useTriggerDeterrence,
  type DeterrenceDevice,
  type DeterrenceTestState,
} from "@visioni/a3s-deterrence/app";
import { track } from "@visioni/a3s-analytics";
import { cameraScope } from "@src/features/cameras/analytics";
import { useTenantId } from "@src/features/tenants";
import { requestErrorType } from "@src/services/api/error-type";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { DeterrenceTestFailed, DeterrenceTestTriggered } from "./analytics";

export interface DeterrenceTest {
  /** Whether a device's test is unavailable: a command in flight, or one sent moments ago. */
  isBusy: (device: DeterrenceDevice) => boolean;
  /** Sends a trigger command for one device. Confirm with the user first. */
  send: (device: DeterrenceDevice) => void;
}

/**
 * Sends test trigger commands for one camera's sirens and strobes, and holds
 * each device's button after a send so a double-tap cannot fire it twice.
 *
 * Holds belong to the camera they were sent for. This screen is reused across
 * cameras when only the route params change: a switch starts clean, and a
 * response for a camera no longer shown never applies a hold to the new one.
 *
 * The ref is the source of truth and is updated synchronously before the
 * request goes out, so two queued confirmations cannot both fire.
 *
 * Shared by the web card and the native settings rows; each layout owns its
 * own confirmation, worded by `deterrenceTestPrompt`. Analytics is tracked here,
 * once per command sent, so both platforms send the same events.
 */
export function useDeterrenceTest(siteId: string | undefined, cameraKey: string | undefined): DeterrenceTest {
  const trigger = useTriggerDeterrence();
  const tenantId = useTenantId();
  const subject = `${siteId ?? ""}/${cameraKey ?? ""}`;
  const state = useRef<DeterrenceTestState>(emptyDeterrenceTestState(subject));
  const [snapshot, setSnapshot] = useState<DeterrenceTestState>(state.current);
  const releaseTimers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const commit = (next: DeterrenceTestState): void => {
    if (next === state.current) return;
    state.current = next;
    setSnapshot(next);
  };

  useEffect(() => {
    const timers = releaseTimers.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const send = async (device: DeterrenceDevice): Promise<void> => {
    if (!siteId || !cameraKey) return;
    const started = startDeterrenceTest(state.current, subject, device, Date.now());
    if (!started) return;
    commit(started);
    const sentFor = subject;
    const scope = cameraScope(tenantId, siteId, cameraKey);
    try {
      await trigger.mutateAsync({ siteId, cameraKey, devices: [device] });
    } catch (error: unknown) {
      track(DeterrenceTestFailed, { ...scope, device, error_type: requestErrorType(error) });
      showErrorToast({ error });
      commit(finishDeterrenceTest(state.current, sentFor, device, false, Date.now()));
      return;
    }
    track(DeterrenceTestTriggered, { ...scope, device });
    // Outside the command's try, so a UI failure here is not reported as a failed test;
    // caught so the press still finishes and `send`'s fire-and-forget promise never rejects.
    try {
      showSuccessToast({ message: DETERRENCE_TEST_SENT_MESSAGE });
    } catch (error: unknown) {
      console.error("[Deterrence] Success toast failed after the test was sent:", error);
    }
    commit(finishDeterrenceTest(state.current, sentFor, device, true, Date.now()));
    const timer = setTimeout(() => {
      releaseTimers.current.delete(timer);
      commit(releaseLapsedDeterrenceTests(state.current, Date.now()));
    }, DETERRENCE_TEST_HOLD_MS);
    releaseTimers.current.add(timer);
  };

  return {
    isBusy: (device) => isDeterrenceTestBusy(snapshot, subject, device, Date.now()),
    send: (device) => {
      void send(device);
    },
  };
}
